import { type SearchDocumentNames } from "@prismicio/editor-fields"
import {
  createContext,
  type ReactNode,
  type TransitionStartFunction,
  useContext,
  useTransition
} from "react"
import { useSearchParams } from "react-router-dom"
import * as z from "zod"

import { useRouteParams } from "../app/routing"
import { useRepository } from "../app/services/repository"

export type SearchDocumentParams = { [S in SearchDocumentNames]?: string[] }

interface DocumentListContext {
  searchDocumentParams: SearchDocumentParams
  updateSearchDocumentParams: (params: SearchDocumentParams) => void
  isLoadingDocuments: boolean
  startTransition: TransitionStartFunction
  urlSearchParams: string
}

export const DocumentListContext = createContext<
  DocumentListContext | undefined
>(undefined)

interface Props {
  children: ReactNode
}

export function DocumentListProvider(props: Props) {
  const { children } = props

  const [_, setSearchDocumentParams] = useSearchParams()

  const { search: searchDocumentParams } = useRouteParams(routeParamsSchema)

  const [isLoadingDocuments, startTransition] = useTransition()

  function updateSearchDocumentParams(
    newSearchDocumentParams: SearchDocumentParams
  ) {
    const filterParams = updateFilterParams(
      newSearchDocumentParams,
      searchDocumentParams
    )

    return startTransition(() =>
      setSearchDocumentParams({
        ...searchDocumentParams,
        ...newSearchDocumentParams,
        ...filterParams
      })
    )
  }

  const urlSearchParams = window.location.search

  return (
    <DocumentListContext.Provider
      value={{
        searchDocumentParams,
        updateSearchDocumentParams,
        isLoadingDocuments,
        startTransition,
        urlSearchParams
      }}
    >
      {children}
    </DocumentListContext.Provider>
  )
}

const routeParamsSchema = {
  search: {
    term: z
      .string()
      .transform((term) => [term])
      .optional(),
    language: z
      .string()
      .transform((language) => [language])
      .optional(),
    customTypes: z.string().transform(splitCommaSeparatedString).optional(),
    tags: z.string().transform(splitCommaSeparatedString).optional(),
    authors: z.string().transform(splitCommaSeparatedString).optional()
  }
}

export function useSearchDocumentParams() {
  const documentListContext = useContext(DocumentListContext)

  if (!documentListContext)
    throw new Error(
      "DocumentListContext must be called from within the DocumentListProvider"
    )

  return documentListContext
}

export function useRepositoryLanguages() {
  const langs = useRepository().languages

  const masterLocale = langs.find((language) => language.is_master)

  if (!masterLocale) throw new Error("Cannot find a master locale.")

  return { masterLocaleId: masterLocale.id, langs }
}

// takes a csv string and creats an array
function splitCommaSeparatedString(customTypes: string) {
  if (customTypes === "") return undefined
  return customTypes.split(",")
}

const filterTypes = ["customTypes", "tags", "authors"] as const

/**
 * Updates the filter params based on the new search params
 * @param newSearchDocumentParams
 * @param currentDocumentParams
 * @returns serialized filter params ready to be used in the url
 */
export function updateFilterParams(
  newSearchDocumentParams: SearchDocumentParams,
  currentDocumentParams: SearchDocumentParams
): SearchDocumentParams {
  const filterParams: SearchDocumentParams = {}

  for (const filterType of filterTypes) {
    const newFilter = newSearchDocumentParams[filterType]
    const currentFilter = currentDocumentParams[filterType]

    if (newFilter) {
      filterParams[filterType] = newFilter.length ? [newFilter.toString()] : []
    } else if (currentFilter && currentFilter.length > 0) {
      filterParams[filterType] = [currentFilter.toString()]
    }
  }

  return filterParams
}
